import React, { useState } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import { useAuth } from './AuthProvider';

type Mode = 'login' | 'signup' | 'new-password';

const LoginPage: React.FC = () => {
  const { login, register, setNewPassword } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPwConfirm, setNewPwConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.newPasswordRequired) {
      setMode('new-password');
    } else if (!result.success) {
      setError(result.error || 'Login failed');
    }
  };

  const handleSignUp = async () => {
    if (password !== confirmPw) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    const result = await register(email, password);
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Sign-up failed');
    }
  };

  const handleNewPassword = async () => {
    if (newPw !== newPwConfirm) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setLoading(true);
    const result = await setNewPassword(newPw);
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Failed to set new password');
    }
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setError('');
  };

  return (
    <Box padding={{ top: 'xxxl' }}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        <Container header={<Header variant="h1">Migration Portal</Header>}>
          <SpaceBetween size="l">
            {error && <Alert type="error">{error}</Alert>}

            {mode === 'login' && (
              <SpaceBetween size="m">
                <FormField label="Email">
                  <Input
                    type="email"
                    value={email}
                    onChange={({ detail }) => setEmail(detail.value)}
                    placeholder="you@company.com"
                    autoFocus
                  />
                </FormField>
                <FormField label="Password">
                  <Input
                    type="password"
                    value={password}
                    onChange={({ detail }) => setPassword(detail.value)}
                    onKeyDown={({ detail }) => { if (detail.key === 'Enter') handleLogin(); }}
                  />
                </FormField>
                <Button variant="primary" fullWidth loading={loading} onClick={handleLogin}>
                  Sign in
                </Button>
                <Box textAlign="center">
                  <Link onFollow={() => switchMode('signup')}>Create an account</Link>
                </Box>
              </SpaceBetween>
            )}

            {mode === 'signup' && (
              <SpaceBetween size="m">
                <Alert type="info">
                  Sign-up is restricted to approved company email addresses.
                </Alert>
                <FormField label="Email">
                  <Input
                    type="email"
                    value={email}
                    onChange={({ detail }) => setEmail(detail.value)}
                    placeholder="you@company.com"
                    autoFocus
                  />
                </FormField>
                <FormField label="Password" description="Min 12 chars, upper + lower + digit + symbol">
                  <Input
                    type="password"
                    value={password}
                    onChange={({ detail }) => setPassword(detail.value)}
                  />
                </FormField>
                <FormField label="Confirm password">
                  <Input
                    type="password"
                    value={confirmPw}
                    onChange={({ detail }) => setConfirmPw(detail.value)}
                    onKeyDown={({ detail }) => { if (detail.key === 'Enter') handleSignUp(); }}
                  />
                </FormField>
                <Button variant="primary" fullWidth loading={loading} onClick={handleSignUp}>
                  Create account
                </Button>
                <Box textAlign="center">
                  <Link onFollow={() => switchMode('login')}>Back to sign in</Link>
                </Box>
              </SpaceBetween>
            )}

            {mode === 'new-password' && (
              <SpaceBetween size="m">
                <Alert type="info">You must set a new password to continue.</Alert>
                <FormField label="New password">
                  <Input
                    type="password"
                    value={newPw}
                    onChange={({ detail }) => setNewPw(detail.value)}
                    autoFocus
                  />
                </FormField>
                <FormField label="Confirm new password">
                  <Input
                    type="password"
                    value={newPwConfirm}
                    onChange={({ detail }) => setNewPwConfirm(detail.value)}
                    onKeyDown={({ detail }) => { if (detail.key === 'Enter') handleNewPassword(); }}
                  />
                </FormField>
                <Button variant="primary" fullWidth loading={loading} onClick={handleNewPassword}>
                  Set password
                </Button>
              </SpaceBetween>
            )}
          </SpaceBetween>
        </Container>
      </div>
    </Box>
  );
};

export default LoginPage;
